<template>
  <div
    v-viewport-width
    :class="['spotlight-container', isPromo && 'with-background'].filter(Boolean).join(' ')"
    :style="{
      '--background-color': component.background_color,
    }"
  >
    <div
      v-if="isPromo"
      class="spotlight-background"
      :style="{
        '--background-image': `url(${component.background})`,
        '--mobile-background-image': `url(${component.mobile_background || component.background})`
      }"
      @click="backgroundClick"
    >
      <div v-if="isPromo">
        <img
          :src="component.image"
          alt=""
          class="img-fluid"
        >
        <div v-html="component.promo" />
      </div>
    </div>
    <flicking-carousel
      :id="id"
      class="spotlight-carousel"
      :autoplay="7000"
      :pagination="true"
    >
      <div
        v-for="deal in deals"
        :key="deal.id"
        class="carousel-cell"
      >
        <deal-impression :id="deal.id">
          <div class="carousel-cell-inner">
            <div class="featured-offer-container d-flex flex-row">
              <div
                class="spotlight-image-container"
              >
                <BlackFridayBadge
                  v-if="isBlackFriday(deal)"
                  class="campaign-badge black-friday-badge"
                />
                <SumoDayBadge
                  v-else-if="isSumoDay(deal)"
                  class="campaign-badge sumo-day-badge"
                />
                <NuxtLink
                  class="d-block text-decoration-none"
                  :to="deal.get_absolute_url"
                >
                  <!-- eslint-disable max-len -->
                  <picture class="h-100 w-100">
                    <source
                      media="(max-width: 768px)"
                      :srcset="`${deal.featured_image_url}?width=850&height=566&aspect_ratio=3:2`"
                      :class="{
                        'has-banner': hasBanner(deal),
                        'last-call-margin': isLimitedTimeSale(deal)}"
                      height="566"
                      width="850"
                    >
                    <source
                      media="(max-width: 1200px)"
                      :srcset="`${deal.featured_image_url}?width=530&height=353&aspect_ratio=3:2`"
                      :class="{
                        'has-banner': hasBanner(deal),
                        'last-call-margin': isLimitedTimeSale(deal)}"
                      height="300"
                      width="450"
                    >
                    <img
                      class="carousel-cell-image h-100 w-100"
                      :src="`${deal.featured_image_url}?width=530&height=353&aspect_ratio=3:2`"
                      :alt="deal.public_name"
                      :class="{
                        'has-banner': hasBanner(deal),
                        'last-call-margin': isLimitedTimeSale(deal)}"
                      height="353"
                      width="530"
                    >
                  </picture>

                  <DealBanner
                    class="spotlight-card-banner"
                    :deal="deal"
                    :is-black-friday-last-call="isBlackFridayLastCall(deal)"
                    :is-last-call="isLastCall(deal)"
                    :is-limited-time-sale="isLimitedTimeSale(deal)"
                  />
                </NuxtLink>
              </div>
              <div class="spotlight-offer-detail">
                <div>
                  <span
                    class="bg-legacy-primary text-white rounded d-inline-block spotlight-banner"
                  >
                    Spotlight
                  </span>
                </div>
                <NuxtLink
                  :to="deal.get_absolute_url"
                  class="featured-header"
                >
                  {{ deal.public_name }}
                </NuxtLink>
                <deal-plan-type :deal="deal" />
                <p class="featured-sub-header">
                  {{ generateCardDescription(deal) | fullstop }}
                </p>
                <taco-rating
                  :class="['sumo-taco-rating', 'featured-deal-taco-container']"
                  :deal="deal"
                  :is-deal-card="true"
                />
                <deal-price
                  class="spotlight-carousel-component"
                  :price="deal.price"
                  :original-price="deal.original_price"
                  :is-free="deal.is_free"
                />
                <div class="buy">
                  <NuxtLink
                    :to="`${deal.get_absolute_url}?ref=spotlight-learn-more`"
                  >
                    Learn more
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </deal-impression>
      </div>
    </flicking-carousel>
  </div>
</template>
<script>
import { DEAL_PLACEHOLDER_IMG } from '@/constants/global';
import get from 'lodash/get';
import BlackFridayBadge from '~/static/campaigns/black-friday-badge-bg.svg?inline';
import SumoDayBadge from '~/static/sumoday-badge.svg?inline';
import DealBanner from '../utilities/DealBanner.vue';
import CampaignsMixin from '~/mixins/campaigns';
import FlickingCarousel from '../FlickingCarousel.vue';
import DealImpression from './DealImpression.vue';
import DealPlanType from '../DealPlanType.vue';
import DealPrice from './DealPrice.vue';
import TacoRating from '../utilities/TacoRating.vue';

export default {
  components: {
    BlackFridayBadge,
    FlickingCarousel,
    SumoDayBadge,
    DealBanner,
    DealImpression,
    DealPlanType,
    DealPrice,
    TacoRating,
  },
  filters: {
    fullstop(value) {
      if (!value) {
        return '';
      }

      if (value && value.length && value.substr(-1, 1) === '.') {
        return value;
      }

      return `${value}.`;
    },
  },
  mixins: [CampaignsMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    deals: {
      type: Array,
      required: true,
    },
    more: {
      type: String,
      required: true,
    },
    component: {
      type: Object,
      required: true,
    },
  },
  head() {
    return {
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: `${this.carouselInitialImage}`,
          imagesrcset: `${this.preloadImageSrcSet}`,
          imagesizes: '(max-width: 768px) 100vw, 20vw',
        },
      ],
    };
  },
  computed: {
    lazyPlaceholderImage() {
      return DEAL_PLACEHOLDER_IMG;
    },
    carouselOptions() {
      return {
        prevNextButtons: true,
        adaptiveHeight: true,
      };
    },
    isPromo() {
      return !!this.component.background;
    },
    campaigns() {
      if (this.$store && this.$store.getters) {
        return this.$store.getters.campaigns || [];
      }
      return [];
    },
    /**
     * return the url of the image to preload for the carousel if available (
     * which is the featured image of the first deal)
     */
    carouselInitialImage() {
      const carouselInitialImage = this.deals?.[0]?.featured_image_url;
      if (carouselInitialImage) {
        return carouselInitialImage;
      }

      return '';
    },
    /**
     * return the imagesrcset value for preloading images based on viewport
     * size and the featured_image_url of the first deal in the carousel
     */
    preloadImageSrcSet() {
      if (this.carouselInitialImage) {
        return `${this.carouselInitialImage}?width=530&height=353&aspect_ratio=3:2 530w,
                ${this.carouselInitialImage}?width=850&height=566&aspect_ratio=3:2 850w`;
      }

      return '';
    },
  },
  methods: {
    backgroundClick(e) {
      if (e.target.nodeName.toLowerCase() !== 'a') {
        window.location = this.more;
      }
    },
    generateCardDescription(deal) {
      return get(deal, 'card_description');
    },
  },
};
</script>

<style lang="scss" >
@import '~/styles/utils/breakpoints';

@include media-breakpoint-down(sm) {
  // Reduce top blank space only if spotlight is the first item
  .sumo-browse-main > section > .smart-collection-section:first-child .with-background {
    margin: -25px -20px 0;
    padding-top: 400px;
  }
}
</style>

<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';

.campaign-badge {
  position: absolute;

  &.black-friday-badge,
  &.sumo-day-badge {
    width: 115px;
    height: 32px;
    top: 8px;
    right: 8px;
  }
}

.spotlight-carousel {
  --taco-height: 14px;
  --taco-width: 100px;

  max-width: var(--viewport-width, 100vw);
  background: var(--background-color, #09292b);
  padding: 40px 0;
  cursor: unset;
}

.deal-plan-type {
  text-transform: lowercase;
  display: inline-block;
  letter-spacing: 0;
}

.deal-plan-type::first-letter {
  text-transform: capitalize;
}

.spotlight-container.with-background {
  margin: -24px -30px 30px;
  padding: 113px 0 38px;
  background-color: transparent;
  padding-top: 325px;
  position: relative;
}

.spotlight-container .spotlight-background {
  background-image: var(--background-image);
  background-repeat: no-repeat;
  background-position: top center;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 90%;
  cursor: pointer;
}

.spotlight-container .spotlight-background > div {
  display: flex;
  justify-content: center;
  height: 325px;
  align-items: center;
  flex-direction: column;
  padding: 20px 0;
}

.spotlight-container .spotlight-background > div img {
  margin: 20px 0;
}

.spotlight-container .spotlight-background > div > div {
  font-family: 'Roboto', sans-serif !important;
  max-width: 700px;
}

.spotlight-container .spotlight-background > div ::v-deep(p) {
  color: #fff;
  font-size: 18px;
  line-height: 27px !important;
  text-align: center;
}

.spotlight-container {
  margin: -24px -30px 30px;
  padding: 38px 0;
  background: var(--background-color, #09292b);
  position: relative;
}

.spotlight-container.with-background .spotlight-carousel {
  max-width: 1000px;
  padding: 40px 0;
  margin: 0 auto;
}

// Fix blinking links on chromium browsers on windows
.spotlight-container :deep(.appsumo-rating-link) {
  backface-visibility: hidden;
}

@include media-breakpoint-up(md) {
  /*
  Need to make the no review text white in the taco-rating component for
  component for visability when the background color is black (medium size and up)
  */
  .spotlight-carousel :deep(.no-taco-rating) {
    color: #fff !important;
  }
}

.spotlight-container.with-background ::v-deep(.spotlight-carousel .deal-price p) {
  color: #fff;
}

.spotlight-carousel .flickity-viewport {
  overflow: hidden;
}

.spotlight-carousel .flicking-button {
  background: rgb(63, 83, 85);
}

.spotlight-container:not(.with-background) {
  /* purgecss ignore */
  ::v-deep .spotlight-carousel .flicking-arrow-prev {
    left: 10px;
  }

  /* purgecss ignore */
  ::v-deep .spotlight-carousel .flicking-arrow-next {
    right: 10px;
  }
}

.spotlight-carousel .featured-offer-container {
  border: 0;
  align-items: center;
  position: relative;
}

.spotlight-carousel .spotlight-image-container {
  order: 2;
  width: 100%;
  margin: 0;
}

.spotlight-carousel .spotlight-image-container a img {
  border-radius: 4px;
  width: 100%;
}

.spotlight-carousel .flicking-pagination {
  bottom: -10px;
}

.spotlight-container.with-background .spotlight-carousel .flicking-pagination {
  bottom: -30px;
}

.spotlight-carousel .featured-header,
.spotlight-carousel .featured-sub-header {
  color: #fff;
}

.spotlight-container .spotlight-banner {
  padding: 4px 8px;
  margin-bottom: 16px;
}

.spotlight-carousel .buy {
  margin: 20px 0 10px;
}

.spotlight-carousel .buy a {
  background: #ffbc00;
  border-radius: 40px;
  color: #1b1b1b;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  padding: 16px 40px;
  text-decoration: none;
}

.spotlight-carousel .featured-header:hover {
  color: #fff;
}

.spotlight-carousel .spotlight-offer-detail {
  width: 40%;
  margin-right: 40px;
  display: flex;
  flex-direction: column;
  min-width: 350px;
  max-width: 600px;
  justify-content: center;
}

.spotlight-carousel .carousel-cell-inner {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 40px;
}

.spotlight-carousel .carousel-cell {
  padding: 0;
  width: var(--viewport-width, 100vw);
  max-width: 2000px;
}

.spotlight-container.with-background .carousel-cell {
  max-width: 1000px;
}

.spotlight-carousel .spotlight-offer-detail .sumo-taco-rating {
  margin-top: 10px;
}

.spotlight-carousel .spotlight-offer-detail .featured-sub-header {
  margin-bottom: 0;
}

@include media-breakpoint-up(sm) {
  .featured-header:hover,
  .featured-header:active,
  .featured-header:focus {
    color: #699cff !important;
  }

  .spotlight-container font {
    display: inline-block;
  }
}

/* Hide buttons for md breakpoint */
@include media-breakpoint-down(md) {
  .spotlight-container:not(.with-background) {
    ::v-deep .spotlight-carousel {
      .flicking-arrow-prev,
      .flicking-arrow-next {
        display: none;
      }
    }
  }
}

@include media-breakpoint-down(sm) {
  .spotlight-container {
    margin: 0 -20px;
    background-color: #fff;
    padding-top: 0;
    overflow: hidden;

    ::v-deep .spotlight-carousel {
      .flicking-viewport {
        width: calc(var(--viewport-width, 100vw) - 40px);
      }
    }
  }

  .spotlight-container.with-background {
    margin: 0 -20px;
    padding-top: 400px;
  }

  .spotlight-carousel .carousel-cell-inner {
    padding: 0;
  }

  .spotlight-container:not(.with-background) .spotlight-carousel {
    background-color: #fff;
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 0;
  }

  .spotlight-container.with-background .spotlight-carousel {
    padding-left: 20px;
    padding-right: 20px;
  }

  .spotlight-container .spotlight-banner {
    top: 10px;
    left: 10px;
    position: absolute;
  }

  .spotlight-carousel .spotlight-image-container {
    order: 1;
    margin: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .spotlight-carousel .carousel-cell {
    padding: 0;
    width: calc(var(--viewport-width, 100vw) - 40px);
    margin-right: 20px;
  }

  .spotlight-carousel .spotlight-offer-detail {
    padding: 36px 0 0;
    order: 2;
    margin-right: 0;
  }

  .spotlight-container .spotlight-background {
    background-image: var(--mobile-background-image);
    background-size: 100% 410px;
  }

  .spotlight-container .spotlight-background > div {
    height: 390px;
  }

  .spotlight-container .spotlight-background > div img {
    padding: 0 20px;
  }

  .spotlight-container .spotlight-background > div ::v-deep(p) {
    width: 100%;
    padding: 0 20px;
  }

  .spotlight-container:not(.with-background) .spotlight-carousel .featured-header,
  .spotlight-container:not(.with-background) .spotlight-carousel .featured-sub-header,
  .spotlight-container:not(.with-background) .spotlight-carousel .featured-header:hover {
    color: #021414;
  }

  .spotlight-carousel .buy a {
    font-size: 14px;
  }
}

@media (width: 768px) {
  .spotlight-image-container {
    max-width: 40%;
  }
}

/* Extra padding prevents overlap of arrows right before md breakpoint */
@media (max-width: 1050px) and (min-width: 992px) {
  .spotlight-carousel .carousel-cell-inner {
    padding: 0 60px;
  }
}

.spotlight-card-banner {
  position: initial !important;
  border-radius: 0;
}

.has-banner {
  position: relative;
  margin-bottom: -29px;
  z-index: -1;
}

.last-call-margin {
  margin-top: -29px;
}

</style>
